import { Box, Stack } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1LoadingButton } from 'components/core/button/index'
import { C1Icon } from 'components/core/icons/C1Icon'
import { RecommendationReviewTooltip } from 'components/core/insights'
import { TaskPrimaryActionsV2Variant } from 'components/core/task-list-v2/task-list-v2-components/TaskPrimaryActionsV2'
import { ApproveIcon } from 'components/pages/task/action/button'
import { ApproveText } from 'components/pages/task/action/messages'
import { ApproveWithCommentTaskDialog } from 'components/pages/task/details/task-content-v2/common/ApproveWithCommentTaskDialog'
import { canActOn } from 'components/pages/task/state'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { useSimpleModal } from 'lib/modals'
import { Check, LockOpen } from 'lucide-react'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { FC, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

export const ApproveWithStepPrimaryAction: FC<{ task: TaskValue, variant?: TaskPrimaryActionsV2Variant }> = ({ task, variant = 'default' }) => {
    const intl = useIntl()
    const { open } = useSimpleModal()
    const toast = useAlert()
    const ticketActionTracker = useTicketActionTracker()

    const handleClick = useCallback(async (): Promise<unknown> => {
        const instance = task.policy?.current?.instance
        const requiresComment =
            instance && instance.oneofKind === 'approval' && instance.approval.approval?.requireApprovalReason
        if (requiresComment) {
            return open(ApproveWithCommentTaskDialog, { task, useStepUp: true })()
        }
        try {
            await ticketActionTracker?.approveWithStepUp(task)
        } catch (err) {
            toast(
                intl.formatMessage(
                    {
                        id: 'task_approve_with_step_up_error',
                        defaultMessage: 'Error approving task with step-up authentication. {code}: {msg}',
                    },
                    {
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    },
                ),
                'error',
            )
        }
        return Promise.resolve()
    }, [intl, open, task, ticketActionTracker, toast])

    if (!canActOn(task, TaskActionType.APPROVE_WITH_STEP_UP)) {
        return null
    }

    return (
        <RecommendationReviewTooltip task={task}>
            <Box>
                <C1LoadingButton
                    startIcon={<Stack direction="row" spacing={1} alignItems="center">
                        <C1Icon icon={LockOpen} size="sm" />
                        <ApproveIcon task={task} />
                    </Stack>}
                    variant={variant === 'contained' ? 'contained' : 'outlined'}
                    key={`components_task_list_approve_with_step_up_option_${task.id}`}
                    onClick={handleClick}
                    loading={!ticketActionTracker}
                    color={variant === 'default' ? 'primary' : 'success'}
                    sx={variant === 'icon-button' ? { minWidth: 0 } : {}}
                >
                    {variant === 'icon-button' ? <C1Icon icon={Check} /> : <ApproveText task={task} requiresStepUp />}
                </C1LoadingButton>
            </Box>
        </RecommendationReviewTooltip>
    )
}
